import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAreas } from './useAreas';
import { usePrevious } from './usePrevious';
import type { StaffOnlyFormData } from '../components/elements/StaffOnlyFilterForm';
import type { StaffOnlySearchParams } from '../components/pages/StaffOnlyResults';
import type { SpaceType } from '@/constants/space';
import { ROUTE_PATH } from '@/constants/routes';

export const useStaffOnlyLogic = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  // 結果画面から戻ってきた時の検索条件（初回のみ取得）
  const initialParams = (location.state?.searchParams ?? undefined) as
    | StaffOnlySearchParams
    | undefined;

  // location.stateをクリア（リロード時の重複復元を防ぐ）
  useEffect(() => {
    if (initialParams) {
      navigate('.', { replace: true, state: {} });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // エラー状態管理
  const [searchError, setSearchError] = useState<string | null>(null);

  // フォーム管理（defaultValuesで初期値を直接設定）
  const { register, watch, setValue } = useForm<StaffOnlyFormData>({
    mode: 'onChange',
    defaultValues: {
      spaceType: initialParams?.space_type ?? '',
      airport: initialParams?.airport ?? '',
      flightType: initialParams?.flight_type ?? '',
      terminal: initialParams?.terminal ?? '',
      lounge: initialParams?.lounge ?? '',
      areaIds: [],
      time: '',
      searchDate: initialParams?.search_date ?? '',
      startsAtHour: initialParams?.starts_at_hour ?? '',
      startsAtMinute: initialParams?.starts_at_minute ?? '',
      endsAtHour: initialParams?.ends_at_hour ?? '',
      endsAtMinute: initialParams?.ends_at_minute ?? '',
      reservationNumberSearch: initialParams?.reservation_no ?? '',
      customerNameSearch: initialParams?.name ?? '',
    },
  });

  // useAreasフックを初期化
  const {
    airports,
    allTerminals,
    allLounges,
    isLoadingAirports,
    isLoadingAllTerminals,
    isLoadingAllLounges,
    error: areaError,
    fetchAirports,
    fetchAllTerminals,
    fetchAllLounges,
  } = useAreas();

  // フォーム値を監視
  const selectedSpaceType = watch('spaceType');
  const selectedAirport = watch('airport');
  const selectedFlightType = watch('flightType');
  const selectedTerminal = watch('terminal');
  const selectedLounge = watch('lounge');
  const selectedDate = watch('searchDate');
  const startsAtHour = watch('startsAtHour');
  const startsAtMinute = watch('startsAtMinute');
  const endsAtHour = watch('endsAtHour');
  const endsAtMinute = watch('endsAtMinute');

  // 前回値を追跡（リセット判定用）
  const airportKey = `${selectedAirport}-${selectedFlightType}`;
  const prevAirportKey = usePrevious(airportKey);
  const prevTerminal = usePrevious(selectedTerminal);

  // スペースタイプ以外の検索条件をクリア
  const clearSearchConditions = useCallback(() => {
    setValue('airport', '');
    setValue('flightType', '');
    setValue('terminal', '');
    setValue('lounge', '');
    setValue('areaIds', []);
    setValue('time', '');
    setValue('searchDate', '');
    setValue('startsAtHour', '');
    setValue('startsAtMinute', '');
    setValue('endsAtHour', '');
    setValue('endsAtMinute', '');
    setValue('reservationNumberSearch', '');
    setValue('customerNameSearch', '');
    setSearchError(null);
  }, [setValue]);

  // 検索ボタンハンドラー
  const handleSearch = useCallback(() => {
    const formData = watch();

    // バリデーション: 時間の整合性チェック（分だけ入力で時間が未入力はエラー）
    const hasStartsAtMinute = formData.startsAtMinute !== '';
    const hasStartsAtHour = formData.startsAtHour !== '';
    const hasEndsAtMinute = formData.endsAtMinute !== '';
    const hasEndsAtHour = formData.endsAtHour !== '';

    if (hasStartsAtMinute && !hasStartsAtHour) {
      setSearchError(t('staffOnly.errors.selectStartsAtHour'));
      return;
    }
    if (hasEndsAtMinute && !hasEndsAtHour) {
      setSearchError(t('staffOnly.errors.selectEndsAtHour'));
      return;
    }

    setSearchError(null);

    // 検索パラメータを構築
    const searchParams: StaffOnlySearchParams = {
      space_type: formData.spaceType,
      airport: formData.airport || undefined,
      flight_type: formData.flightType || undefined,
      terminal: formData.terminal || undefined,
      lounge: formData.lounge || undefined,
      search_date: formData.searchDate || undefined,
      starts_at_hour: formData.startsAtHour || undefined,
      starts_at_minute: formData.startsAtMinute || undefined,
      ends_at_hour: formData.endsAtHour || undefined,
      ends_at_minute: formData.endsAtMinute || undefined,
      reservation_no: formData.reservationNumberSearch || undefined,
      name: formData.customerNameSearch || undefined,
    };

    // 結果画面に遷移
    navigate(ROUTE_PATH.STAFF_ONLY_RESULTS, { state: { searchParams } });
  }, [navigate, t, watch]);

  // リセットボタンハンドラー
  const handleReset = useCallback(() => {
    setValue('spaceType', '');
    clearSearchConditions();
    window.scroll({ top: 0, behavior: 'smooth' });
  }, [clearSearchConditions, setValue]);

  // スペースタイプ変更ハンドラー
  const onChangeSpaceType = useCallback(
    (select: SpaceType) => {
      if (select !== selectedSpaceType) {
        // シャワーか防音個室の選択が変えられたら検索条件はリセット
        clearSearchConditions();
      }
      setValue('spaceType', select);
    },
    [clearSearchConditions, selectedSpaceType, setValue],
  );

  // 空港リストを取得（スペースタイプ変更時）
  useEffect(() => {
    fetchAirports(selectedSpaceType);
  }, [fetchAirports, selectedSpaceType]);

  // ターミナルリストを取得（空港選択に連動）
  useEffect(() => {
    if (selectedSpaceType) {
      fetchAllTerminals(
        selectedSpaceType,
        selectedAirport || undefined,
        selectedFlightType || undefined,
      );
    }
  }, [
    fetchAllTerminals,
    selectedSpaceType,
    selectedAirport,
    selectedFlightType,
  ]);

  // ラウンジリストを取得（ターミナル選択に連動）
  useEffect(() => {
    if (selectedSpaceType) {
      fetchAllLounges(
        selectedSpaceType,
        selectedAirport || undefined,
        selectedFlightType || undefined,
        selectedTerminal || undefined,
      );
    }
  }, [
    fetchAllLounges,
    selectedSpaceType,
    selectedAirport,
    selectedFlightType,
    selectedTerminal,
  ]);

  // 空港変更時にターミナル・ラウンジをリセット
  useEffect(() => {
    if (prevAirportKey !== undefined && prevAirportKey !== airportKey) {
      setValue('terminal', '');
      setValue('lounge', '');
    }
  }, [airportKey, prevAirportKey, setValue]);

  // ターミナル変更時にラウンジをリセット
  useEffect(() => {
    if (prevTerminal !== undefined && prevTerminal !== selectedTerminal) {
      setValue('lounge', '');
    }
  }, [selectedTerminal, prevTerminal, setValue]);

  // 空港が1つだけの場合は自動選択
  useEffect(() => {
    if (airports.length === 1 && !isLoadingAirports) {
      setValue('airport', airports[0].airport);
      setValue('flightType', airports[0].flightType);
    }
  }, [airports, isLoadingAirports, setValue]);

  // ターミナルが1つだけの場合は自動選択
  useEffect(() => {
    if (allTerminals.length === 1 && !isLoadingAllTerminals) {
      setValue('terminal', allTerminals[0]);
    }
  }, [allTerminals, isLoadingAllTerminals, setValue]);

  // ラウンジが1つだけの場合は自動選択
  useEffect(() => {
    if (allLounges.length === 1 && !isLoadingAllLounges) {
      setValue('lounge', allLounges[0]);
    }
  }, [allLounges, isLoadingAllLounges, setValue]);

  // 時間関連パラメータが復元されているかチェック
  const hasTimeParams = Boolean(
    initialParams?.search_date ||
      initialParams?.starts_at_hour ||
      initialParams?.ends_at_hour,
  );

  return {
    // フォーム
    register,
    setValue,
    // 選択値
    selectedSpaceType,
    selectedAirport,
    selectedFlightType,
    selectedTerminal,
    selectedLounge,
    selectedDate,
    startsAtHour,
    startsAtMinute,
    endsAtHour,
    endsAtMinute,
    // ドロップダウンデータ
    airports,
    allTerminals,
    allLounges,
    isLoadingAirports,
    isLoadingAllTerminals,
    isLoadingAllLounges,
    // エラー
    areaError,
    searchError,
    // ハンドラー
    handleSearch,
    handleReset,
    onChangeSpaceType,
    // その他
    hasTimeParams,
  };
};
